import React, { useEffect } from "react";
import { X } from "@phosphor-icons/react";

/**
 * FormModal — accessible centre-screen dialog for the customer/trader waitlist
 * forms. Closes on ESC, backdrop click, and locks body scroll while open.
 */
export default function FormModal({ open, onClose, title, subtitle, children, testId }) {
  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = orig;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId || "form-modal"}
    >
      <div
        className="relative w-full sm:max-w-2xl bg-[var(--sand)] rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 sm:px-7 pt-5 sm:pt-6 pb-3 border-b" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-black text-xl sm:text-2xl leading-tight" style={{ color: "var(--brand-green)" }}>
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-[var(--text-muted)] mt-1">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-3 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5 transition"
            data-testid="form-modal-close"
          >
            <X size={20} weight="bold" style={{ color: "var(--text-muted)" }} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 sm:px-7 py-5">
          {children}
        </div>
      </div>
      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-modal-in { animation: modal-in 0.25s cubic-bezier(0.2,0.9,0.2,1); }
      `}</style>
    </div>
  );
}
